import type { RecommendationResponse } from '@/client/types.gen'
import {
  getDashboardAppScoutDashboardGetOptions,
  getRecommendationByPublicIdAppInboxRecommendationsByPublicIdPublicIdGetOptions,
  getRecommendationCountAppInboxRecommendationsCountGetOptions,
  listRecommendationsAppInboxRecommendationsGetInfiniteOptions,
  listRecommendationsAppInboxRecommendationsGetQueryKey,
} from '@/client/@tanstack/react-query.gen'
import { SkeletonList } from '@/components/skeleton-common'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { useAnalytics } from '@/hooks/use-analytics'
import { useToast } from '@/hooks/use-toast'
import { useAutoTour } from '@/hooks/use-tour'
import { getInboxItemAnalyticsProps } from '@/lib/analytics/inbox'
import { isTourLocked } from '@/lib/tours/utils/tour-lock'
import { cn } from '@/lib/utils'
import type { QueryClient } from '@tanstack/react-query'
import { useIsFetching, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronDown,
  Filter,
  Inbox,
  Mail,
  RefreshCw,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DetailPanel } from './-components/detail-panel'
import { InboxList } from './-components/inbox-list'
import type { InboxSearch, StatusFilterValue } from './-hooks/use-inbox-filters'
import { useInboxFilters } from './-hooks/use-inbox-filters'
import { useInboxGenerations } from './-hooks/use-inbox-generations'
import { useInboxInvalidation } from './-hooks/use-inbox-invalidation'
import { useInboxMarkRead } from './-hooks/use-inbox-mark-read'
import { scoutHandoffLeadCount, scoutHandoffToastTitle } from './-utils/scout-handoff'
import { INBOX_SOURCES, VALID_SOURCES } from './-constants/inbox-sources'

const SOURCE_OPTIONS = Object.entries(INBOX_SOURCES).map(([value, config]) => ({
  value,
  label: config.label,
  icon: config.icon,
}))

const STATUS_OPTIONS: { value: StatusFilterValue, label: string, icon: typeof CheckCircle2 }[] = [
  { value: 'NOT_DONE', label: 'To Do', icon: Inbox },
  { value: 'ACTIONED', label: 'Done', icon: CheckCircle2 },
]

function StatusFilterPill({
  value,
  counts,
  onChange,
}: {
  value: StatusFilterValue
  counts: { pending: number, actioned: number }
  onChange: (v: StatusFilterValue) => void
}) {
  const [open, setOpen] = useState(false)
  const activeOption = STATUS_OPTIONS.find(o => o.value === value)!

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-tour="inbox-status-filter"
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs transition-colors',
            value === 'NOT_DONE'
              ? 'border-border bg-background text-muted-foreground hover:bg-muted/50 hover:text-foreground'
              : 'border-primary/30 bg-primary/5 text-primary hover:bg-primary/10',
          )}
        >
          {(() => {
            const Icon = activeOption.icon
            return <Icon className="h-3 w-3" />
          })()}
          <span className="font-medium">{activeOption.label}</span>
          {value === 'NOT_DONE'
            ? <ChevronDown className="h-3 w-3 opacity-50" />
            : (
                <span
                  role="button"
                  tabIndex={0}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-primary/20"
                  aria-label="Reset to active"
                  onClick={(e) => {
                    e.stopPropagation()
                    onChange('NOT_DONE')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation()
                      onChange('NOT_DONE')
                    }
                  }}
                >
                  <X className="h-3 w-3" />
                </span>
              )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-1" align="start">
        {STATUS_OPTIONS.map((opt) => {
          const Icon = opt.icon
          const countMap = { NOT_DONE: counts.pending, ACTIONED: counts.actioned }
          const count = countMap[opt.value]
          const isActive = value === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              className={cn(
                'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors',
                isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
              )}
              onClick={() => {
                onChange(opt.value)
                setOpen(false)
              }}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 text-left">{opt.label}</span>
              {count > 0 && (
                <span className="text-xs text-muted-foreground">{count}</span>
              )}
              {isActive && <Check className="h-3.5 w-3.5 shrink-0" />}
            </button>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}

function SourceFilterPill({
  value,
  bySource,
  onChange,
  onClear,
}: {
  value: string | undefined
  bySource: Record<string, number>
  onChange: (v: string) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  const activeOption = SOURCE_OPTIONS.find(o => o.value === value)

  // Any tour can request that this popover stay open via the generic
  // 'source-filter' lock (lib/tours/utils/tour-lock). When the lock is held
  // we suppress all close-attempts on this popover (outside-click, escape,
  // autofocus shifts) so the user can keep clicking through sources without
  // the dropdown flickering closed and reopening.
  const handleOpenChange = (next: boolean) => {
    if (!next && isTourLocked('source-filter')) {
      return
    }
    setOpen(next)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-tour="inbox-source-filter"
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs transition-colors',
            value
              ? 'border-primary/30 bg-primary/5 text-primary hover:bg-primary/10'
              : 'border-border bg-background text-muted-foreground hover:bg-muted/50 hover:text-foreground',
          )}
        >
          <Filter className="h-3 w-3" />
          <span className="font-medium">Source</span>
          {activeOption && (
            <>
              <span className="text-primary/50">is</span>
              <span className="font-semibold">{activeOption.label}</span>
              <span
                role="button"
                tabIndex={0}
                className="ml-0.5 rounded-full p-0.5 hover:bg-primary/20"
                aria-label="Clear source filter"
                onClick={(e) => {
                  e.stopPropagation()
                  onClear()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation()
                    onClear()
                  }
                }}
              >
                <X className="h-3 w-3" />
              </span>
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" align="start">
        {SOURCE_OPTIONS.map((opt) => {
          const Icon = opt.icon
          const count = bySource[opt.value] ?? 0
          const isActive = value === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              data-source-row="true"
              data-source-value={opt.value}
              data-source-label={opt.label}
              className={cn(
                'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors',
                isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
              )}
              onClick={() => {
                // A tour holding the 'source-filter' lock installs
                // capture-phase pointerdown + click + keydown interceptors
                // on these rows that call stopImmediatePropagation, so this
                // React handler never runs while that lock is held — no
                // explicit guard needed.
                if (isActive) {
                  onClear()
                }
                else {
                  onChange(opt.value)
                }
                setOpen(false)
              }}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 text-left">{opt.label}</span>
              {count > 0 && (
                <span className="text-xs text-muted-foreground">{count}</span>
              )}
              {isActive && <Check className="h-3.5 w-3.5 shrink-0" />}
            </button>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}

function UnreadFilterPill({
  active,
  unreadCount,
  onChange,
}: {
  active: boolean
  unreadCount: number
  onChange: (v: boolean) => void
}) {
  return (
    <button
      type="button"
      data-tour="inbox-unread-filter"
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs transition-colors',
        active
          ? 'border-primary/30 bg-primary/5 text-primary hover:bg-primary/10'
          : 'border-border bg-background text-muted-foreground hover:bg-muted/50 hover:text-foreground',
      )}
      onClick={() => onChange(!active)}
    >
      <Mail className="h-3 w-3" />
      <span className="font-medium">Unread</span>
      {unreadCount > 0 && (
        <span className={cn('text-[10px]', active ? 'text-primary/60' : 'text-muted-foreground')}>
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
      {active && (
        <span
          role="button"
          tabIndex={0}
          className="ml-0.5 rounded-full p-0.5 hover:bg-primary/20"
          aria-label="Clear unread filter"
          onClick={(e) => {
            e.stopPropagation()
            onChange(false)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation()
              onChange(false)
            }
          }}
        >
          <X className="h-3 w-3" />
        </span>
      )}
    </button>
  )
}

function InboxPage() {
  const navigate = useNavigate({ from: '/inbox' })
  const scrollRef = useRef<HTMLDivElement>(null)
  const { capture } = useAnalytics()
  const { toast } = useToast()
  const openedItemsRef = useRef(new Set<string>())
  useAutoTour('inbox-intro')

  // Filters (URL-driven)
  const {
    search,
    statusFilter,
    sourceFilter,
    readFilter,
    updateSearch: rawUpdateSearch,
    clearAllFilters: rawClearAllFilters,
    hasNonDefaultFilter,
  } = useInboxFilters(scrollRef)

  // Emit inbox_viewed once per mount with the filter context.
  const viewedRef = useRef(false)
  useEffect(() => {
    if (viewedRef.current)
      return
    viewedRef.current = true
    capture('inbox_viewed', {
      filter_status: statusFilter,
      source_filter_count: sourceFilter ? 1 : 0,
    })
  }, [capture, statusFilter, sourceFilter])

  // Selection state
  const [listSelectedRec, setListSelectedRec] = useState<RecommendationResponse | undefined>()

  // Wrap filter changes to also clear selection and emit a funnel event.
  const updateSearch = useCallback(
    (updates: Partial<InboxSearch>, clearItem = true) => {
      rawUpdateSearch(updates, clearItem)
      if (clearItem)
        setListSelectedRec(undefined)
      if (updates.status !== undefined) {
        capture('inbox_filter_applied', { filter_type: 'status', value: updates.status ?? 'NOT_DONE' })
      }
      if (updates.source !== undefined) {
        capture('inbox_filter_applied', { filter_type: 'source', value: updates.source ?? '' })
      }
      if (updates.unread !== undefined) {
        capture('inbox_filter_applied', { filter_type: 'unread', value: updates.unread ? 'unread' : '' })
      }
    },
    [rawUpdateSearch, capture],
  )
  const clearAllFilters = useCallback(() => {
    rawClearAllFilters()
    setListSelectedRec(undefined)
  }, [rawClearAllFilters])

  const deepLinkedItem = useQuery({
    ...getRecommendationByPublicIdAppInboxRecommendationsByPublicIdPublicIdGetOptions({
      path: { public_id: search.item! },
    }),
    enabled: !!search.item,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  // Scout dashboard — only on the homepage handoff, to label the toast with the
  // live pregenerated count (the same number the card showed and the item lists),
  // not the inbox item's frozen account snapshot. Cached from the homepage nav.
  const scoutDashboard = useQuery({
    ...getDashboardAppScoutDashboardGetOptions(),
    enabled: search.from === 'home',
    refetchOnWindowFocus: false,
  })

  const selectedRec = search.item ? (deepLinkedItem.data ?? listSelectedRec) : listSelectedRec

  const captureItemOpened = useCallback((rec: RecommendationResponse, isDeepLink: boolean, surface: 'list' | 'detail') => {
    const key = `${rec.id}:${isDeepLink ? 'deep_link' : 'list'}`
    if (openedItemsRef.current.has(key))
      return
    openedItemsRef.current.add(key)
    capture('inbox_item_opened', getInboxItemAnalyticsProps(rec, {
      is_deep_link: isDeepLink,
      action_surface: surface,
    }))
  }, [capture])

  // Auto-match filters when deep-linked item resolves with mismatched status
  const hasAutoMatchedRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!deepLinkedItem.data || !search.item)
      return
    if (hasAutoMatchedRef.current === search.item)
      return
    hasAutoMatchedRef.current = search.item

    const itemStatus = deepLinkedItem.data.status as StatusFilterValue
    if (itemStatus !== statusFilter) {
      void navigate({
        search: (prev: InboxSearch) => ({
          ...prev,
          status: itemStatus === 'ACTIONED' ? ('ACTIONED' as const) : undefined,
        }),
        replace: true,
      })
    }
  }, [deepLinkedItem.data, search.item, statusFilter, navigate])

  // Mark-as-read
  const { markItemRead } = useInboxMarkRead({
    onMarkedRead: (rec) => {
      const isDeepLink = search.item === rec.publicId
      capture('inbox_item_read', getInboxItemAnalyticsProps(rec, {
        is_deep_link: isDeepLink,
        action_surface: isDeepLink ? 'detail' : 'list',
      }))
    },
  })
  useEffect(() => {
    if (deepLinkedItem.data && search.item) {
      if (listSelectedRec?.publicId !== search.item) {
        captureItemOpened(deepLinkedItem.data, true, 'detail')
      }
      markItemRead(deepLinkedItem.data)
    }
  }, [captureItemOpened, deepLinkedItem.data, listSelectedRec?.publicId, search.item, markItemRead])

  // Confirm the homepage → inbox Scout-leads handoff (ENG-3517). Fires once, then
  // strips ?from so a refresh/back doesn't re-toast. When deep-linked to a specific
  // item we wait for it to resolve so we can name the lead count; the source-filter
  // fallback (no item) toasts immediately with generic copy.
  const scoutHandoffToastedRef = useRef(false)
  useEffect(() => {
    if (search.from !== 'home' || scoutHandoffToastedRef.current)
      return
    if (search.item && deepLinkedItem.isLoading)
      return
    scoutHandoffToastedRef.current = true
    const leadCount = scoutHandoffLeadCount(
      scoutDashboard.data?.pregeneratedCount,
      deepLinkedItem.data?.accountIds.length,
    )
    toast({
      title: scoutHandoffToastTitle(leadCount),
      description: 'Review and claim them below — they\'ll be added to your team\'s accounts.',
      variant: 'success',
      className: 'w-fit ml-auto',
    })
    capture('inbox_scout_handoff_landed', {
      lead_count: leadCount ?? 0,
      deep_linked: !!search.item,
    })
    void navigate({
      search: (prev: InboxSearch) => ({ ...prev, from: undefined }),
      replace: true,
    })
  }, [search.from, search.item, deepLinkedItem.isLoading, deepLinkedItem.data, scoutDashboard.data?.pregeneratedCount, toast, capture, navigate])

  // Generation polling
  const { statusByRec, onGenerateStarted } = useInboxGenerations(
    selectedRec?.isMultiAccountActionable === true ? selectedRec.id : undefined,
  )

  // Selection handlers
  const handleItemSelect = useCallback(
    (rec: RecommendationResponse) => {
      setListSelectedRec(rec)
      captureItemOpened(rec, false, 'list')
      markItemRead(rec)
      void navigate({
        search: (prev: InboxSearch) => ({ ...prev, item: rec.publicId }),
        replace: true,
      })
    },
    [captureItemOpened, navigate, markItemRead],
  )

  const handleItemDeselect = useCallback(() => {
    setListSelectedRec(undefined)
    void navigate({
      search: (prev: InboxSearch) => ({ ...prev, item: undefined }),
      replace: true,
    })
  }, [navigate])

  // Counts — always fetch fresh on mount (entering inbox page).
  // This replaces the old loader invalidation approach which caused
  // multiple fetches because invalidation triggers all observers.
  const { data: countData } = useQuery({
    ...getRecommendationCountAppInboxRecommendationsCountGetOptions(),
    staleTime: 20 * 60_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  })

  const pendingCount = countData?.pending ?? 0
  const bySource = countData?.bySource ?? {}
  const actionedCount = countData?.actioned ?? 0
  const unreadCount = countData?.unread ?? 0

  // Refresh
  const { invalidateListAndCount } = useInboxInvalidation()
  const refreshInbox = invalidateListAndCount

  const isRefreshingInbox = useIsFetching({
    queryKey: listRecommendationsAppInboxRecommendationsGetQueryKey(),
  }) > 0

  // Determine what to show in the detail panel
  const deepLinkError = search.item && deepLinkedItem.isError
  const deepLinkLoading = search.item && deepLinkedItem.isLoading

  // On mobile we collapse to a single column: show the list by default and
  // swap to the detail when an item is selected (with a back button).
  const isDetailVisible = !!selectedRec || !!deepLinkLoading || !!deepLinkError

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Left panel — list. Full width on mobile, fixed 480px on md+.
          Hidden on mobile when a detail is open. */}
      <div
        data-tour="inbox-list-panel"
        className={cn(
          'flex w-full shrink-0 flex-col border-r border-border md:w-120 md:flex',
          isDetailVisible && 'hidden',
        )}
      >
        {/* Filter bar */}
        <div data-tour="inbox-filter-bar" className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
          <StatusFilterPill
            value={statusFilter}
            counts={{ pending: pendingCount, actioned: actionedCount }}
            onChange={v => updateSearch({ status: v === 'ACTIONED' ? 'ACTIONED' : undefined })}
          />
          <SourceFilterPill
            value={sourceFilter}
            bySource={bySource}
            onChange={v => updateSearch({ source: v })}
            onClear={() => updateSearch({ source: undefined })}
          />
          {statusFilter === 'NOT_DONE' && (
            <UnreadFilterPill
              active={readFilter === false}
              unreadCount={unreadCount}
              onChange={v => updateSearch({ unread: v ? true : undefined })}
            />
          )}
          <div className="flex-1" />
          <Button
            data-tour="inbox-refresh"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            onClick={refreshInbox}
            disabled={isRefreshingInbox}
            aria-label="Refresh inbox"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isRefreshingInbox && 'animate-spin')} />
          </Button>
          {hasNonDefaultFilter && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
              onClick={clearAllFilters}
            >
              Clear
            </Button>
          )}
        </div>

        {/* List */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          <InboxList
            status={statusFilter}
            sourceFilter={sourceFilter}
            isReadFilter={statusFilter === 'NOT_DONE' ? readFilter : undefined}
            selectedId={selectedRec?.id}
            onSelect={handleItemSelect}
            onDeselect={handleItemDeselect}
            expectedCount={
              statusFilter === 'NOT_DONE'
                ? (sourceFilter ? (bySource[sourceFilter] ?? 0) : pendingCount)
                : actionedCount
            }
          />
        </div>
      </div>

      {/* Right panel — detail. Full width on mobile when an item is selected
          (with a back button); always visible on md+. */}
      <div
        data-tour="inbox-detail-panel"
        className={cn(
          'flex flex-1 flex-col overflow-hidden md:flex',
          !isDetailVisible && 'hidden md:flex',
        )}
      >
        {/* Mobile-only back-to-list button */}
        {isDetailVisible && (
          <div className="flex items-center border-b border-border px-2 py-2 md:hidden">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2 text-sm"
              onClick={handleItemDeselect}
            >
              <ArrowLeft className="h-4 w-4" />
              Inbox
            </Button>
          </div>
        )}
        <div className="flex-1 overflow-hidden">
          {deepLinkLoading && (
            <div className="space-y-4 p-6">
              <div className="space-y-2">
                <Skeleton className="h-6 w-64" />
                <Skeleton className="h-4 w-40" />
              </div>
              <SkeletonList items={4} />
            </div>
          )}
          {!deepLinkLoading && deepLinkError && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <X className="h-6 w-6 text-destructive" />
              </div>
              <p className="text-sm font-medium text-foreground">
                This inbox item is no longer available
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                It may have been removed or you may not have access.
              </p>
            </div>
          )}
          {!deepLinkLoading && !deepLinkError && selectedRec && (
            <DetailPanel
              recommendation={selectedRec}
              onResolve={handleItemDeselect}
              generatedMap={statusByRec[selectedRec.id] ?? {}}
              onGenerateStarted={onGenerateStarted}
            />
          )}
          {!deepLinkLoading && !deepLinkError && !selectedRec && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Inbox className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">
                Select a recommendation to view details
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const PAGE_SIZE = 20

function loaderPrefetchList(
  queryClient: QueryClient,
  status: string,
  source?: string,
  isRead?: boolean,
) {
  const generatedOptions = listRecommendationsAppInboxRecommendationsGetInfiniteOptions({
    query: { status, source, is_read: isRead, limit: PAGE_SIZE },
  })
  queryClient.prefetchInfiniteQuery({
    queryKey: generatedOptions.queryKey,
    queryFn: generatedOptions.queryFn,
    staleTime: 30_000,
    // Must match the component's useInfiniteQuery initialPageParam (undefined)
    // so loader and component share the same cache entry.
    initialPageParam: undefined as unknown as null,
    pages: 1,
    // eslint-disable-next-line unicorn/no-useless-undefined
    getNextPageParam: () => undefined,
  })
}

export const Route = createFileRoute('/_authed/_user/inbox/')({
  component: InboxPage,
  validateSearch: (raw: Record<string, unknown>): InboxSearch => {
    const s = raw as Partial<{ item: string, status: string, source: string, unread: boolean | string, from: string }>
    return {
      item: typeof s.item === 'string' ? s.item : undefined,
      status: s.status === 'ACTIONED' ? 'ACTIONED' : undefined,
      source: typeof s.source === 'string' && VALID_SOURCES.includes(s.source)
        ? s.source : undefined,
      unread: s.unread === true || s.unread === 'true' ? true : undefined,
      from: s.from === 'home' ? 'home' : undefined,
    }
  },
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps }) => {
    const { search } = deps
    const status = search.status ?? 'NOT_DONE'
    const source = search.source
    const isRead = search.unread ? false : undefined
    loaderPrefetchList(context.queryClient, status, source, isRead)

    // Prefetch deep-linked item to avoid waterfall
    if (search.item) {
      context.queryClient.prefetchQuery({
        ...getRecommendationByPublicIdAppInboxRecommendationsByPublicIdPublicIdGetOptions({
          path: { public_id: search.item },
        }),
        staleTime: 30_000,
      })
    }

    // Count is handled by the component's useQuery with refetchOnMount: 'always'
    // rather than loader invalidation, to avoid triggering multiple observers.
  },
})
